import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { isSessionEnd, loginUrl, type ApiError } from '../lib/api'
import { useI18n, type MessageKey } from '../lib/i18n'
import type { SaveState } from '../lib/useAutosave'
import { Icon, type IconName } from './Icon'

/** A control's tooltip: its name, and below it what it does. */
export const tooltip = (label: string, hint?: string) => (hint ? `${label}\n${hint}` : label)

/**
 * A compact button showing only its icon; its label names it and heads its tooltip. With `pressed` it is a
 * toggle; with `expanded` it shows and hides what `controls` names.
 */
export function IconButton({ icon, label, hint, pressed, expanded, controls, onClick, className }: {
  icon: IconName
  label: string
  hint?: string
  pressed?: boolean
  expanded?: boolean
  controls?: string
  onClick: () => void
  className?: string
}) {
  return (
    <button
      type="button"
      className={className ? `secondary compact icon-only ${className}` : 'secondary compact icon-only'}
      aria-label={label}
      aria-pressed={pressed}
      aria-expanded={expanded}
      aria-controls={controls}
      title={tooltip(label, hint)}
      onClick={onClick}
    >
      <Icon name={icon} />
    </button>
  )
}

export function ErrorBox({ error, retry, image = false }: { error?: ApiError; retry?: () => void; image?: boolean }) {
  const { t } = useI18n()
  if (!error) return null
  return (
    <div className="alert" role="alert">
      {isSessionEnd(error) ? (
        <>
          {t('sessionEnded')} <a href={loginUrl()}>{t('loginAgain')}</a>
        </>
      ) : (
        image ? <><p>{t('imageRenderFailed')}</p><details><summary>{t('errorDetails')}</summary>{error.message}</details></> : error.message
      )}
      {error.requestId && <p className="small">{t('errorReference')}: <code>{error.requestId}</code></p>}
      {retry && !isSessionEnd(error) && <button type="button" className="secondary" onClick={retry}>{t('retry')}</button>}
    </div>
  )
}

/** A wide table that scrolls on its own on a narrow screen, so the page never scrolls sideways; named and reachable by keyboard. */
export function TableScroll({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="table-scroll" role="region" aria-label={label} tabIndex={0}>
      {children}
    </div>
  )
}

/** A page that does not exist, or that the user may not open: it says so and leads back to the images. */
export function NotFound() {
  const { t } = useI18n()
  return (
    <>
      <h1>{t('notFoundTitle')}</h1>
      <div className="card empty-state">
        <p className="muted">{t('notFound')}</p>
        <Link className="button" to="/images">
          {t('browseImages')}
        </Link>
      </div>
    </>
  )
}

export function Section({ title, children, actions }: { title: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="card">
      <header className="card-header">
        <h2>{title}</h2>
        {actions}
      </header>
      {children}
    </section>
  )
}

/**
 * A button that opens the file dialog, worded in the page's language instead of the browser's.
 * The file input inside is hidden from view only, so it stays reachable by keyboard; it is emptied
 * after each choice, so the same file can be chosen again. With `icon` or a face of its own
 * (`children`, then styled by `className`) its label names it for screen readers and as its tooltip.
 */
export function FileButton({
  label,
  onFiles,
  multiple,
  folder,
  accept,
  disabled,
  primary,
  icon,
  className,
  children,
}: {
  label: string
  onFiles: (files: File[]) => void
  multiple?: boolean
  folder?: boolean
  accept?: string
  disabled?: boolean
  primary?: boolean
  icon?: IconName
  className?: string
  children?: ReactNode
}) {
  const face = children ?? (icon && <Icon name={icon} />)
  const look = className ?? `button${primary ? '' : ' secondary'}${icon ? ' compact icon-only' : ''}`
  return (
    <label className={`${look} file-button${disabled ? ' disabled' : ''}`} title={face ? label : undefined}>
      {face ? (
        <>
          {face}
          <span className="visually-hidden">{label}</span>
        </>
      ) : (
        label
      )}
      <input
        type="file"
        className="visually-hidden"
        multiple={multiple}
        accept={accept}
        disabled={disabled}
        {...(folder ? { webkitdirectory: '' } : {})}
        onChange={(event) => {
          onFiles([...(event.target.files ?? [])])
          event.target.value = ''
        }}
      />
    </label>
  )
}

const SAVE_STATES: Record<SaveState, MessageKey> = {
  idle: 'autosaveIdle',
  editing: 'autosaveEditing',
  saving: 'save_saving',
  saved: 'save_saved',
  failed: 'save_failed',
}

/** Where a draft that saves itself stands (lib/useAutosave.ts), in words beside the section's title. */
export function AutosaveStatus({ state }: { state: SaveState }) {
  const { t } = useI18n()
  return (
    <span className={`save-state ${state}`} role="status">
      {state === 'saved' && <Icon name="check" />}
      {t(SAVE_STATES[state])}
    </span>
  )
}

export function Field({ label, children, hint, wide }: { label: string; children: ReactNode; hint?: string; wide?: boolean }) {
  return (
    <label className={wide ? 'field full' : 'field'}>
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  )
}

/** A destructive action that needs a second click, instead of a native browser dialog; `compact` for toolbars. */
export function ConfirmButton({
  label,
  warning,
  onConfirm,
  disabled,
  compact,
}: {
  label: string
  warning?: string
  onConfirm: () => void
  disabled?: boolean
  compact?: boolean
}) {
  const { t } = useI18n()
  const [asking, setAsking] = useState(false)
  const size = compact ? ' compact' : ''
  if (!asking) {
    return (
      <button type="button" className={`danger${size}`} disabled={disabled} onClick={() => setAsking(true)}>
        {label}
      </button>
    )
  }
  return (
    <span className="confirm">
      {warning && <span className="warning-text">{warning}</span>}
      <button
        type="button"
        className={`danger${size}`}
        onClick={() => {
          setAsking(false)
          onConfirm()
        }}
      >
        {t('confirm')}
      </button>
      <button type="button" className={`secondary${size}`} onClick={() => setAsking(false)}>
        {t('cancel')}
      </button>
    </span>
  )
}

/** A form's Save and Cancel, in the row `className` lays out; Save submits the form. */
export function SaveCancel({ className, disabled = false, onCancel }: { className: string; disabled?: boolean; onCancel: () => void }) {
  const { t } = useI18n()
  return (
    <div className={className}>
      <button type="submit" disabled={disabled}>
        {t('save')}
      </button>
      <button type="button" className="secondary" onClick={onCancel}>
        {t('cancel')}
      </button>
    </div>
  )
}

/** Back and on through what is shown, which `status` names, such as "Page 2 of 5"; a side without a step is disabled. */
export function Pager({ status, onPrevious, onNext }: { status: string; onPrevious?: () => void; onNext?: () => void }) {
  const { t } = useI18n()
  return (
    <nav className="toolbar" aria-label={status}>
      <button type="button" className="secondary compact" disabled={!onPrevious} onClick={onPrevious}>
        {t('previous')}
      </button>
      <span className="muted small">{status}</span>
      <button type="button" className="secondary compact" disabled={!onNext} onClick={onNext}>
        {t('next')}
      </button>
    </nav>
  )
}

/** Copies the text to the clipboard; where the browser refuses (no secure context, no permission), it says so. */
export function CopyButton({ text }: { text: string }) {
  const { t } = useI18n()
  const [state, setState] = useState<'copy' | 'copied' | 'copyFailed'>('copy')
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setState('copied')
    } catch {
      setState('copyFailed')
    }
  }
  return (
    <button type="button" className="secondary" onClick={() => void copy()}>
      {t(state)}
    </button>
  )
}

/** Marks an annotation set that came with an uploaded file (UPL-09). */
export function ImportedMark({ author }: { author: string | null | undefined }) {
  const { t } = useI18n()
  if (author === null || author === undefined) return null
  return <span className="badge quiet">{author ? t('importedFrom', { author }) : t('importedSet')}</span>
}

export function formatBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit += 1
  }
  return `${value.toFixed(unit ? 1 : 0)} ${units[unit]}`
}

export function formatDate(value: string | null, lang: string): string {
  return value ? new Date(value).toLocaleDateString(lang) : '—'
}

/** A date with its time of day, for events of which a day may hold several, such as uploads. */
export function formatDateTime(value: string | null, lang: string): string {
  return value ? new Date(value).toLocaleString(lang, { dateStyle: 'medium', timeStyle: 'short' }) : '—'
}
